/* global process */
// `process.env.NODE_ENV` is string-replaced by Vite at build time; the global
// comment is how firebase.js, analytics.js and tab-navigation.js declare it.
import { Capacitor } from '@capacitor/core';
import { router } from '@inertiajs/react';
import { checkSessionOnResume } from '@/lib/session-resume';
import { exchangeNativeOauth } from '@/lib/native-oauth';
import { deepLinkPath, isAppPath, isBareReturn } from '@/lib/native-deep-link';

// Dev-only: ?platform=android or ?platform=ios in the URL forces platform
// detection for testing native-like UI in a browser preview.
//
// The NODE_ENV gate is load-bearing, not tidiness: isNative() below returns
// true for ANY value of this override, so on production web a link to
// /app/upgrade?platform=ios would render the RevenueCat paywall — a native-only
// purchase flow that cannot complete in a browser — instead of Stripe checkout.
// `process.env.NODE_ENV` (not import.meta.env) because Vite string-replaces it
// at build time and jest can still parse it.
const DEV_PLATFORM_OVERRIDE =
  process.env.NODE_ENV !== 'production' && typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('platform')
    : null;

if (DEV_PLATFORM_OVERRIDE) {
  document.documentElement.classList.add(DEV_PLATFORM_OVERRIDE);
}

export function isNative() {
  return !!DEV_PLATFORM_OVERRIDE || Capacitor.isNativePlatform();
}

export function isAndroid() {
  if (DEV_PLATFORM_OVERRIDE) return DEV_PLATFORM_OVERRIDE === 'android';
  return isNative() && Capacitor.getPlatform() === 'android';
}

export function isIOS() {
  if (DEV_PLATFORM_OVERRIDE) return DEV_PLATFORM_OVERRIDE === 'ios';
  return isNative() && Capacitor.getPlatform() === 'ios';
}

// The app's start screen — the new-chat window — mirroring the backend's
// `app_start_path` (Authentication concern).
const APP_START_PATH = '/app/chats/new';
// A free user is redirected off /app/chats/new to the tour (AI is Plus-only,
// RequireAiAccess), so their real home is the tour's first slide
// (TourCatalog::DEFAULT_PAGE). Seed it directly and skip the redirect round-trip.
const FREE_START_PATH = '/tour/bible';

// Where the native back-swipe "home" history entry should point (see appUrlOpen).
// Only divert when aiAccess is *explicitly* false — mirroring lib/ai-access's
// hasAiAccess. A missing/stale value falls back to /app/chats/new, which the
// server still redirects correctly for a free user, so the fallback is safe.
export function backSwipeStartPath(aiAccess) {
  return aiAccess === false ? FREE_START_PATH : APP_START_PATH;
}

// Latest `currentUser.aiAccess` seen on an Inertia page. The native deep-link
// handler has no page/route context of its own, so it reads this to seed the
// right back-swipe home. Updated only when an event carries currentUser, so a
// partial reload that omits the shared prop never clobbers a known value.
let latestAiAccess;

function trackAiAccess(event) {
  const user = event?.detail?.page?.props?.currentUser;
  if (user && 'aiAccess' in user) latestAiAccess = user.aiAccess;
}

// Who the last page was rendered for, and its localized login route — read by
// the resume session probe (see checkSessionOnResume). Only pages that carry
// the shared props update it, so a partial reload can't flip a known state.
const sessionState = { signedIn: false, loginPath: '/login' };

export function trackSession(event) {
  const props = event?.detail?.page?.props;
  if (!props?.routes) return;
  sessionState.signedIn = Boolean(props.currentUser);
  if (props.routes.login) sessionState.loginPath = props.routes.login;
}

// The first page's navigate event fires before initCapacitor registers its
// router listeners (they wait on plugin imports), so seed from it directly —
// otherwise a cold-started, signed-in app would look signed out to the resume
// probe until the user navigated.
export function seedFromInitialPage(initialPage) {
  const event = { detail: { page: initialPage } };
  trackAiAccess(event);
  trackSession(event);
}

export function currentSessionState() {
  return { ...sessionState };
}

// A deep link arriving around a resume owns the navigation. Android delivers
// it just before 'resume' (onNewIntent precedes onResume), iOS just after, so
// the guard is a window on either side rather than an ordering.
export const DEEP_LINK_GRACE_MS = 10_000;
let lastDeepLinkAt = -Infinity;

export function noteDeepLink(now = Date.now()) {
  lastDeepLinkAt = now;
}

export function deepLinkOwnsNavigation(now = Date.now()) {
  return now - lastDeepLinkAt < DEEP_LINK_GRACE_MS;
}

export async function initCapacitor(initialPage) {
  // Skip native plugin init when running in browser with ?platform= override —
  // the override only affects UI branching (isAndroid/isIOS), not native APIs.
  if (!Capacitor.isNativePlatform()) return;

  seedFromInitialPage(initialPage);

  // Add platform class to <html> for CSS-based platform targeting (.android / .ios)
  document.documentElement.classList.add(Capacitor.getPlatform());

  const { StatusBar, Style } = await import('@capacitor/status-bar');
  const { SplashScreen } = await import('@capacitor/splash-screen');

  // Lazy-load NavigationBar only on Android (iOS doesn't have a software nav bar)
  let NavigationBar;
  if (Capacitor.getPlatform() === 'android') {
    const mod = await import('@capgo/capacitor-navigation-bar');
    NavigationBar = mod.NavigationBar;
  }

  const resolveBackgroundColor = () => {
    const raw = window
      .getComputedStyle(document.documentElement)
      .getPropertyValue('--background')
      .trim();
    const temp = document.createElement('div');
    temp.style.color = raw;
    document.body.appendChild(temp);
    const rgb = window.getComputedStyle(temp).color;
    temp.remove();
    const [r, g, b] = rgb.match(/\d+/g).map(Number);
    return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
  };

  const applySystemBarColors = () => {
    const isDark = document.documentElement.classList.contains('dark');
    const override = document.documentElement.dataset.statusBarColor;
    const bgColor = override || resolveBackgroundColor();

    StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light });
    StatusBar.setBackgroundColor({ color: bgColor });

    if (NavigationBar) {
      NavigationBar.setNavigationBarColor({
        color: bgColor,
        darkButtons: !isDark,
      });
    }
  };

  applySystemBarColors();

  const observer = new window.MutationObserver((mutations) => {
    for (const m of mutations) {
      if (
        m.attributeName === 'class' ||
        m.attributeName === 'data-status-bar-color'
      ) {
        applySystemBarColors();
      }
    }
  });
  observer.observe(document.documentElement, { attributes: true });

  // Hide splash after React hydration completes (first page rendered).
  // Wait for the #app element to have children (Inertia mounts content there),
  // preventing the brief white flash between splash and app content.
  const appEl = document.getElementById('app');
  if (appEl && appEl.children.length > 0) {
    SplashScreen.hide();
  } else {
    const splashObserver = new window.MutationObserver(() => {
      if (appEl && appEl.children.length > 0) {
        splashObserver.disconnect();
        // Small delay to let the first paint render before hiding splash
        requestAnimationFrame(() => SplashScreen.hide());
      }
    });
    splashObserver.observe(appEl || document.body, {
      childList: true,
      subtree: true,
    });
    // Safety fallback: hide splash after 5 seconds regardless
    setTimeout(() => {
      splashObserver.disconnect();
      SplashScreen.hide();
    }, 5000);
  }

  // Keyboard: scroll focused input into view and resize viewport
  const { Keyboard } = await import('@capacitor/keyboard');
  // Hide the accessory bar on iOS only — Android users rely on the
  // autocomplete/suggestion strip above the keyboard.
  if (isIOS()) Keyboard.setAccessoryBarVisible({ isVisible: false });
  Keyboard.setScroll({ isDisabled: false });

  let keyboardVisible = false;

  // Reveal the focused field only if the keyboard would cover it. Use
  // 'nearest' (not 'center'): a field already visible above the keyboard —
  // e.g. the chat composer pinned just above the bottom nav — must be left in
  // place. 'center' scrolled it up to the middle of the viewport, leaving a
  // gap between the input and the nav.
  const revealActiveField = () => {
    const active = document.activeElement;
    // Guard: if focus was lost meanwhile, activeElement is <body> and
    // scrollIntoView would yank the page to the top.
    if (
      !active ||
      !active.matches('input, textarea, select, [contenteditable]')
    )
      return;
    active.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  // The keyboard plugin resizes the WebView keyboardAnimationDuration + 0.2s
  // AFTER keyboardWillShow fires (see @capacitor/keyboard Keyboard.m,
  // setKeyboardHeight's delay), so scrolling on the event measures the
  // un-shrunk viewport where the field still looks visible and 'nearest'
  // no-ops — leaving it under the keyboard (e.g. onboarding church search).
  // Reveal after each viewport resize settles (WebKit's own scroll-assist and
  // the late frame change shuffle geometry mid-animation), plus timer passes
  // as a fallback for focus changes that don't resize (keyboard already open)
  // and as a final correction — scrollIntoView('nearest') is idempotent, so
  // extra passes on already-correct geometry are no-ops.
  let cancelPendingReveal = null;
  const revealAfterViewportResize = () => {
    cancelPendingReveal?.();
    const reveal = () => window.requestAnimationFrame(revealActiveField);
    let settleTimer = null;
    const fallback = setTimeout(reveal, 700);
    const onResize = () => {
      // The fallback exists only for focus changes that never resize the
      // viewport (keyboard already open). Once a resize arrives, the settle
      // pass below owns the reveal — a late duplicate pass on the same
      // scroller would abort another module's in-flight smooth scroll (e.g.
      // the place-search dropdown's scrollBy) and strand it half-revealed.
      clearTimeout(fallback);
      clearTimeout(settleTimer);
      settleTimer = setTimeout(reveal, 100);
    };
    const correction = setTimeout(() => {
      cancelPendingReveal?.();
      cancelPendingReveal = null;
      reveal();
    }, 1200);
    window.addEventListener('resize', onResize);
    cancelPendingReveal = () => {
      window.removeEventListener('resize', onResize);
      clearTimeout(settleTimer);
      clearTimeout(fallback);
      clearTimeout(correction);
    };
  };

  Keyboard.addListener('keyboardWillShow', (info) => {
    keyboardVisible = true;
    document.documentElement.style.setProperty(
      '--keyboard-height',
      `${info.keyboardHeight}px`,
    );
    revealAfterViewportResize();
  });

  Keyboard.addListener('keyboardWillHide', () => {
    keyboardVisible = false;
    cancelPendingReveal?.();
    cancelPendingReveal = null;
    document.documentElement.style.setProperty('--keyboard-height', '0px');
  });

  // iOS native behavior: tap outside an input to dismiss the keyboard —
  // but never on scroll/drag. See @/lib/tap-outside-dismiss.
  const { default: installTapOutsideDismiss } =
    await import('@/lib/tap-outside-dismiss');
  installTapOutsideDismiss();

  // Track the current user's AI access so appUrlOpen can seed the right
  // back-swipe home. Both events for the same reason as bindAnalyticsFlush:
  // 'navigate' misses same-URL replaces, 'success' misses the initial load.
  router.on('navigate', trackAiAccess);
  router.on('success', trackAiAccess);
  router.on('navigate', trackSession);
  router.on('success', trackSession);

  const { App } = await import('@capacitor/app');

  // lib/history-interceptor.js skips an entry a closed overlay left behind.
  // When that lands on the tab's first entry, Back has nowhere to go: leave
  // the app, as the back button does there.
  window.addEventListener('bosko:history-start', () => {
    if (isAndroid()) App.exitApp();
  });

  // Android back button: dismiss keyboard first, then navigate back
  App.addListener('backButton', ({ canGoBack }) => {
    if (keyboardVisible) {
      Keyboard.hide();
      return;
    }
    if (canGoBack) {
      window.history.back();
    } else {
      App.exitApp();
    }
  });

  // Validate the session when the app returns from a *real* background.
  //
  // The old code did router.reload() on every appStateChange{isActive:true}.
  // On iOS that maps to UIApplication.didBecomeActive, which fires on any
  // transient interruption (Control Center, notification shade, permission /
  // Face ID sheets, the app switcher) — so the app re-fetched and flashed the
  // progress bar + skeleton "for no reason". The 'pause'/'resume' pair fires
  // ONLY on a genuine background transition, so brief interruptions no longer
  // trigger anything, and we validate the session with an invisible probe
  // instead of a visible reload (see @/lib/session-resume).
  let backgroundedAt = null;
  App.addListener('pause', () => {
    backgroundedAt = Date.now();
  });
  App.addListener('resume', () => {
    router.flushAll();
    const awayMs =
      backgroundedAt == null ? Infinity : Date.now() - backgroundedAt;
    backgroundedAt = null;
    const { signedIn, loginPath } = currentSessionState();
    checkSessionOnResume({
      awayMs,
      signedIn,
      loginPath,
      shouldRedirect: () => !deepLinkOwnsNavigation(),
    });
  });

  // Handle deep links (shared links, OAuth callbacks and the web pages'
  // "Open in Bosko" returns)
  App.addListener('appUrlOpen', handleAppUrlOpen);
}

// What the app does with a URL the OS hands it (appUrlOpen). Exported so the
// routing can be tested without a native shell.
export async function handleAppUrlOpen({ url }) {
  noteDeepLink();
  const path = deepLinkPath(url);

  // OAuth callback: exchange the one-time authorization code using the PKCE
  // verifier retained only by this WebView instance.
  if (path.startsWith('/auth/capacitor-callback')) {
    try {
      const { Browser } = await import('@capacitor/browser');
      await Browser.close();
      const result = await exchangeNativeOauth(path);
      window.location.href = result.redirectUrl;
    } catch {
      window.location.href = '/login';
    }
    return;
  }

  // "Back to the app where I was" (e.g. after confirming an email in the
  // browser): stay on the current screen and refresh its props, rather than
  // restarting a free user's tour or dropping whatever they had open.
  if (isBareReturn(path)) {
    router.reload();
    return;
  }

  // Seed a "home" history entry so swiping back from a deep link has
  // somewhere to go. This is the app's start screen — the new-chat window for
  // a Plus user, the tour for a free user — mirroring the backend's
  // `app_start_path` (Authentication concern) and its Plus-only AI redirect.
  // The routes are hardcoded to their English form on purpose: this module has
  // no access to the localized `routes`, the URL is invisible in the native
  // WebView, and the page re-localizes client-side via useLocaleSync. Safe for
  // non-English users despite the English URL — the nav keys its active state
  // and center button off the Inertia component name (`isNavItemActive` /
  // `isChatSubPage` in @/lib/nav-items), not the URL, so a localized-routes /
  // English-URL mismatch no longer misfires. Keep in sync with app_start_path
  // if the start screen ever moves.
  //
  // The state must be non-null: Inertia 3 treats a null-state popstate as a
  // hash change and just rewrites the entry to the page on screen, so the
  // swipe would go nowhere. A non-null state without `.page` fails its
  // isValidState check, and Inertia then visits the entry's URL (replace),
  // which renders the start screen.
  const startPath = backSwipeStartPath(latestAiAccess);
  if (path !== startPath && isAppPath(path)) {
    window.history.replaceState({ boskoDeepLinkHome: true }, '', startPath);
  }

  // Navigate to the deep link path via Inertia router
  router.visit(path);
}
